import React, {useEffect, useMemo, useState} from 'react'
import {
  formatISODateTime,
  toLocalDateTime,
  toLocalJSDate
} from '../../services/dateUtils'
import {IbeConfig, SearchParams, SearchSpecialRatesParams} from '../../types'
import clsx from 'clsx'
import SearchSpecialRatesControl from './SearchSpecialRatesControl'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faCalendar,
  faChevronDown,
  faHotel,
  faLocationDot,
  faSearch,
  faTicket,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import {getLogger} from '../../services/logging'
import SearchDatePicker from './SearchDatePicker'
import {
  getPropertyConfigsForLocation,
  getPropertyConfigsForRegion,
  isRegionSearch
} from '../../services/utils'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('SearchBar')

interface PropertyOption {
  location: string
  propertyId: string
  propertyName: string
}

function getPropertyOptionsForSelection(
  config: IbeConfig | null,
  locationSelection: string
): Record<string, PropertyOption[]> {
  if (!config?.properties) {
    return {}
  }
  const allProperties = config.properties

  const filteredProperties =
    locationSelection === null || locationSelection === ''
      ? config.properties
      : isRegionSearch(config, locationSelection)
      ? getPropertyConfigsForRegion(allProperties, locationSelection)
      : getPropertyConfigsForLocation(allProperties, locationSelection)

  return filteredProperties.reduce((acc, property) => {
    const location = property.location
    const propertyId = property.propertyApaleoId
    const propertyName = property.shortName || property.name

    if (!acc[location]) {
      acc[location] = []
    }

    acc[location].push({
      location,
      propertyId,
      propertyName
    })

    // sort properties by name
    acc[location].sort((a, b) => {
      if (a.propertyName < b.propertyName) {
        return -1
      }
      if (a.propertyName > b.propertyName) {
        return 1
      }
      return 0
    })

    return acc
  }, {} as Record<string, PropertyOption[]>)
}

export interface SearchBarProps {
  searchParams: SearchParams
  onSubmit: (searchParams: SearchParams) => void
  className?: string
  config: IbeConfig | null
}

const SearchBar = ({
  searchParams,
  onSubmit,
  className = '',
  config
}: SearchBarProps) => {
  const [searchForm, setSearchForm] = useState<SearchParams>(searchParams)
  const [isSearchButtonAnimating, setIsSearchButtonAnimating] = useState(false)

  const locations = config?.locations || []
  const regions = config?.regions || []
  const propertyOptions = useMemo(() => {
    return getPropertyOptionsForSelection(config, searchForm.location)
  }, [config, searchForm.location])

  logger.info({
    message: 'Setting property options...',
    data: {
      location: searchForm.location,
      propertyOptions
    }
  })

  const startDate = searchForm?.dateRange?.start
    ? toLocalJSDate(searchForm.dateRange.start)
    : null
  const endDate = searchForm?.dateRange?.end
    ? toLocalJSDate(searchForm.dateRange.end)
    : null

  const animateSearchButton = () => {
    if (!isSearchButtonAnimating) {
      setIsSearchButtonAnimating(true)
      setTimeout(() => {
        setIsSearchButtonAnimating(false)
      }, 2000)
    }
  }

  useEffect(() => {
    // if location changed, animate the search button
    if (searchParams.location !== searchForm.location) {
      animateSearchButton()
    }

    // only searchParams location takes precedence
    const updatedSearchParams = {
      location: searchParams.location || searchForm.location,
      propertyId: searchParams.propertyId || searchForm.propertyId,
      dateRange: searchParams.dateRange || searchForm.dateRange,
      guests: searchParams.guests || searchForm.guests,
      specialRates: searchParams.specialRates || searchForm.specialRates
    }

    setSearchForm(updatedSearchParams)
  }, [searchParams])

  return (
    <form
      onSubmit={e => {
        e.preventDefault()

        if (searchForm.location) {
          logger.info({message: 'Search form submitted', data: {searchForm}})
          pushDataLayerEvent({
            event: 'Search_Submit',
            category: 'IBE',
            value: 1
          })
          onSubmit(searchForm)
        }
      }}
      className={clsx(
        'bg-white border shadow-md',
        'flex flex-col gap-2 py-3 rounded-3xl',
        'xl:flex-row xl:items-center xl:space-between xl:p-4 xl:rounded-full',
        className
      )}
      aria-label="Room search form."
    >
      <div
        className={clsx(
          'flex-1 px-3',
          'flex flex-col space-between',
          'xl:px-6 xl:flex-row xl:items-start'
        )}
      >
        {/* Location Control */}
        <div className={clsx('form-control location-control')}>
          <div
            className={clsx(
              'text-xs font-medium',
              'hidden xl:flex flex-row items-center gap-2'
            )}
          >
            <FontAwesomeIcon
              icon={faLocationDot}
              className="text-accent"
              title="Location field"
            />
            <span className="text-light-primary">Location</span>
          </div>
          <div className="relative flex items-center">
            <FontAwesomeIcon
              icon={faLocationDot}
              className="text-accent xl:hidden absolute left-3 z-10 pointer-events-none"
              title="Location field"
            />
            <select
              style={{
                appearance: 'none'
              }}
              className={clsx(
                'text-sm font-medium cursor-pointer bg-white',
                'w-full py-2 pl-10 xl:pl-0',
                'xl:py-1 xl:w-52',
                'border-0 focus:border-0 focus:ring-0 outline-none',
                'search-input'
              )}
              data-field="search-location"
              data-clarity-unmask="true"
              value={searchForm.location}
              onChange={e => {
                logger.info({
                  message: 'Location selected',
                  data: {location: e.target.value}
                })

                setSearchForm({
                  ...searchForm,
                  location: e.target.value,
                  propertyId: undefined
                })

                animateSearchButton()
              }}
              tabIndex={0}
              aria-label="Select a location."
              aria-required="true"
            >
              <option
                key="DEFAULT"
                value=""
                disabled
                data-clarity-unmask="true"
              >
                Select a Location
              </option>
              {locations.map(location => (
                <option
                  data-clarity-unmask="true"
                  data-location={location}
                  key={location}
                  value={location}
                >
                  {location}
                </option>
              ))}

              {regions && regions.length > 0 && (
                <optgroup label="Regions" data-clarity-unmask="true">
                  {regions.map(region => (
                    <option
                      data-clarity-unmask="true"
                      data-region={region}
                      key={region}
                      value={region}
                    >
                      {region}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 text-2xs pointer-events-none"
              title="Location field toggle"
            />
          </div>
        </div>

        <div className="divider xl:divider-horizontal mt-0.5 mb-2"></div>

        {/* Property Control */}
        <div className={clsx('form-control property-control')}>
          <div
            className={clsx(
              'text-xs font-medium',
              'hidden xl:flex flex-row items-center gap-2'
            )}
          >
            <FontAwesomeIcon
              icon={faHotel}
              className="text-accent"
              title="Property field"
            />
            <span className="text-light-primary">Property</span>
          </div>
          <div className="relative flex items-center">
            <FontAwesomeIcon
              icon={faHotel}
              className="text-accent xl:hidden absolute left-3 z-10 pointer-events-none"
              title="Property field"
            />
            <select
              style={{
                appearance: 'none'
              }}
              className={clsx(
                'text-sm text-ellipsis font-medium cursor-pointer bg-white',
                'w-full py-2 pl-10 xl:pl-0',
                'xl:py-1 xl:w-40',
                'border-0 focus:border-0 focus:ring-0 outline-none',
                'search-input'
              )}
              data-field="search-property"
              data-clarity-unmask="true"
              value={searchForm.propertyId}
              onChange={e => {
                const selectedPropertyId = e.target.value
                const selectedLocationId = e.target.options[
                  e.target.selectedIndex
                ].dataset.location as string

                logger.info({
                  message: 'Property selected',
                  data: {selectedPropertyId, selectedLocationId, searchForm}
                })

                if (
                  searchForm.location === '' ||
                  searchForm.location === 'all'
                ) {
                  setSearchForm({
                    ...searchForm,
                    location: selectedLocationId || '',
                    propertyId: selectedPropertyId
                  })
                } else {
                  setSearchForm({
                    ...searchForm,
                    propertyId: selectedPropertyId
                  })
                }
              }}
              tabIndex={0}
              aria-label="Select a property."
              aria-required="true"
            >
              <option
                data-location="all"
                data-property="all"
                data-clarity-unmask="true"
                key="all"
                value="all"
              >
                All Properties
              </option>

              {locations.map(location => {
                const propertyOptionsForLocation =
                  propertyOptions[location] || []

                return propertyOptionsForLocation.length > 0 ? (
                  <optgroup
                    key={location}
                    label={location}
                    data-clarity-unmask="true"
                  >
                    {propertyOptionsForLocation.map(property => (
                      <option
                        data-location={property.location}
                        data-property={property.propertyId}
                        data-clarity-unmask="true"
                        key={property.propertyId}
                        value={property.propertyId}
                      >
                        {property.propertyName}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              })}
            </select>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={clsx(
                'absolute top-1/2 transform -translate-y-1/2 right-1',
                'text-2xs pointer-events-none'
              )}
              title="Property field toggle"
            />
          </div>
        </div>

        <div className="divider xl:divider-horizontal mt-0.5 mb-2"></div>

        {/* Date Range Control */}
        <div
          className={clsx('form-control w-full xl:w-auto date-range-control')}
        >
          <div
            className={clsx(
              'text-xs font-medium',
              'hidden xl:flex flex-row items-center gap-2'
            )}
          >
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-accent"
              title="Date range field"
            />
            <span className="text-light-primary">Dates</span>
          </div>
          <div className="relative flex items-center w-full xl:w-auto">
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-accent xl:hidden absolute left-3 z-10 pointer-events-none"
              title="Date range field"
            />
            <SearchDatePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setSearchForm({
                  ...searchForm,
                  dateRange: {
                    type: 'Specific',
                    start: start
                      ? formatISODateTime(toLocalDateTime(start))
                      : undefined,
                    end: end
                      ? formatISODateTime(toLocalDateTime(end))
                      : undefined
                  }
                })
              }}
            />
          </div>
        </div>

        <div className="divider xl:divider-horizontal mt-0.5 mb-2"></div>

        {/* Guests Control */}
        <div className={clsx('form-control guests-control')}>
          <div
            className={clsx(
              'text-xs font-medium',
              'hidden xl:flex flex-row items-center gap-2'
            )}
          >
            <FontAwesomeIcon
              icon={faUser}
              className="text-accent"
              title="Guests field"
            />
            <span className="text-light-primary">Guests</span>
          </div>
          <div className="relative flex items-center">
            <FontAwesomeIcon
              icon={faUser}
              className="text-accent xl:hidden absolute left-3 z-10 pointer-events-none"
              title="Guests field"
            />
            <select
              style={{
                appearance: 'none'
              }}
              className={clsx(
                'text-sm font-medium cursor-pointer bg-white',
                'w-full xl:w-24 py-2 pl-10 xl:pl-0',
                'xl:py-1',
                'border-0 focus:border-0 focus:ring-0 outline-none',
                'search-input'
              )}
              data-field="search-guests"
              data-clarity-unmask="true"
              value={searchForm.guests}
              onChange={e => {
                const guests = parseInt(e.target.value, 10)
                logger.info({
                  message: 'Guests updated',
                  data: {guests}
                })

                setSearchForm({
                  ...searchForm,
                  guests
                })
              }}
              tabIndex={0}
              aria-label="Select the number of guests."
              aria-required="true"
            >
              <option key="1" value={1} data-clarity-unmask="true">
                1 Guest
              </option>
              <option key="2" value={2} data-clarity-unmask="true">
                2 Guests
              </option>
              <option key="3" value={3} data-clarity-unmask="true">
                3 Guests
              </option>
              <option key="4" value={4} data-clarity-unmask="true">
                4 Guests
              </option>
              <option key="5" value={5} data-clarity-unmask="true">
                5 Guests
              </option>
              <option key="6" value={6} data-clarity-unmask="true">
                6 Guests
              </option>
              <option key="7" value={7} data-clarity-unmask="true">
                7 Guests
              </option>
              <option key="8" value={8} data-clarity-unmask="true">
                8 Guests
              </option>
              <option key="9" value={9} data-clarity-unmask="true">
                9 Guests
              </option>
              <option key="10" value={10} data-clarity-unmask="true">
                10 Guests
              </option>
            </select>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 text-2xs pointer-events-none"
              title="Guest field dropdown"
            />
          </div>
        </div>

        <div className="divider xl:divider-horizontal mt-0.5 mb-2"></div>

        {/* Special Rates Control */}
        <div className={clsx('form-control special-rates-control')}>
          <div
            className={clsx(
              'text-xs font-medium',
              'hidden xl:flex flex-row items-center gap-2'
            )}
          >
            <FontAwesomeIcon
              icon={faTicket}
              className="text-accent"
              title="Promo/corp codes field"
            />
            <span className="text-light-primary">Promo/Corp Code</span>
          </div>
          <div className="relative flex items-center pr-1">
            <FontAwesomeIcon
              icon={faTicket}
              className="text-accent xl:hidden absolute left-3 z-10 pointer-events-none"
              title="Promo/corp codes field"
            />
            <SearchSpecialRatesControl
              specialRates={searchForm.specialRates}
              onChange={(specialRates: SearchSpecialRatesParams) => {
                logger.info({
                  message: 'Special rates updated',
                  data: {specialRates}
                })
                setSearchForm({
                  ...searchForm,
                  specialRates
                })
              }}
              data-field="search-special-rates"
              data-clarity-unmask="true"
              aria-label="Enter promo code, corporte code, or select special rate."
              aria-required="false"
            />
          </div>
        </div>
      </div>

      {/* Search Button */}
      <div className={clsx('flex-0', 'xl:mr-2')}>
        <button
          type="submit"
          className={clsx(
            'btn btn-secondary btn-wide rounded-full',
            'flex items-center justify-center',
            'mx-auto w-11/12 my-2',
            'xl:btn-circle xl:my-0 xl:mx-0',
            {'btn-pulse': isSearchButtonAnimating}
          )}
          disabled={!searchForm.location}
          aria-label="Search for available rooms."
        >
          <FontAwesomeIcon
            icon={faSearch}
            className="text-lg"
            title="Submit search"
          />
          <span className="block xl:hidden">Search</span>
        </button>
      </div>
    </form>
  )
}

export default SearchBar
